import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, MapPinIcon, ClockIcon, XMarkIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { useRealtimeStore } from '../store/realtimeStore';
import { format } from 'date-fns';
import clsx from 'clsx';

export default function SOSOverlay() {
  const navigate = useNavigate();
  const { sosEvents, selectSOS } = useRealtimeStore();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [lastSOSCount, setLastSOSCount] = useState(0);
  
  const triggeredSOS = sosEvents.filter(e => e.status === 'TRIGGERED');
  const hasTriggeredSOS = triggeredSOS.length > 0;

  // Auto-expand when new SOS arrives
  useEffect(() => {
    if (triggeredSOS.length > lastSOSCount) {
      setIsMinimized(false);
      setIsDismissed(false);
    }
    setLastSOSCount(triggeredSOS.length);
  }, [triggeredSOS.length, lastSOSCount]);

  if (!hasTriggeredSOS) return null;
  if (isDismissed) return null;

  const handleViewSOS = (sosId: string) => {
    selectSOS(sosId);
    navigate('/sos');
  };

  // Minimized banner view
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 animate-pulse" style={{ zIndex: 9999 }}>
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg shadow-lg border-2 border-red-400 transition-all"
        >
          <ExclamationTriangleIcon className="w-6 h-6" />
          <span className="font-bold">
            {triggeredSOS.length} ACTIVE SOS
          </span>
          <ChevronUpIcon className="w-5 h-5" />
        </button>
      </div>
    );
  }

  // Full alert panel (non-blocking, positioned in corner)
  return (
    <div className="fixed bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)]" style={{ zIndex: 9999 }}>
      <div className="bg-command-panel border-2 border-red-500 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with controls */}
        <div className="flex items-center justify-between p-4 bg-red-600/20 border-b border-red-500/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
              <ExclamationTriangleIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-red-500 animate-pulse">
                SOS EMERGENCY
              </h3>
              <p className="text-xs text-gray-400">
                {triggeredSOS.length} active alert{triggeredSOS.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Minimize"
            >
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            </button>
            <button
              onClick={() => setIsDismissed(true)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Dismiss (will reappear on new SOS)"
            >
              <XMarkIcon className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* SOS List */}
        <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
          {triggeredSOS.map((sos) => (
            <div
              key={sos.id}
              className={clsx(
                'p-3 rounded-lg border',
                'bg-red-500/10 border-red-500/50'
              )}
            >
              <div className="flex items-start justify-between">
                <div>
                  {(() => {
                    const platoon = sos.platoon;
                    const displayName = platoon?.callsign || 'Unknown platoon';
                    return (
                      <>
                        <h4 className="font-semibold text-white text-sm flex items-center gap-1">
                          <span>📍</span>
                          {displayName}
                        </h4>
                        <p className="text-xs text-gray-400">
                          {platoon?.unit?.code || 'Platoon'}
                        </p>
                      </>
                    );
                  })()}
                </div>
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">
                  {sos.triggerType}
                </span>
              </div>

              <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <MapPinIcon className="w-3 h-3" />
                  <span>{sos.latitude?.toFixed(4)}, {sos.longitude?.toFixed(4)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <ClockIcon className="w-3 h-3" />
                  <span>{format(new Date(sos.triggeredAt), 'HH:mm:ss')}</span>
                </div>
              </div>

              <button
                onClick={() => handleViewSOS(sos.id)}
                className="mt-2 w-full py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-bold rounded transition-colors"
              >
                RESPOND NOW
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-3 bg-red-600/10 border-t border-red-500/30">
          <button
            onClick={() => navigate('/sos')}
            className="w-full py-2 bg-command-accent text-command-bg text-sm font-bold rounded-lg hover:bg-command-accent/90 transition-colors"
          >
            Open SOS Command Center
          </button>
        </div>
      </div>
    </div>
  );
}

